import session from '@api/session'
import { APIEndpoint } from '@api/_version'

export default {
/**
 * Fetches a list of integrations, optionally filtering by widget ID.
 * @param {string|null} widgetId - The UUID of the widget to filter integrations by (optional).
 * @returns {Promise} - Promise representing the list of integrations.
 */
listIntegrations(widgetId = null) {
  const queryParams = []

  if (widgetId) {
    queryParams.push(`widget_id=${widgetId}`)
  }

  const queryString = queryParams.length ? '?' + queryParams.join('&') : ''

  return session.get(`${APIEndpoint}/api/integration/${queryString}`)
},

  /**
   * Creates a new integration.
   * @param {Object} integrationData - Data for the new integration.
   * @returns {Promise} - Promise representing the created integration.
   */
  createIntegration(integrationData) {
    if (!integrationData || typeof integrationData !== 'object') {
      throw new Error('Integration creation requires valid data.')
    }

    return session.post(`${APIEndpoint}/api/integration/`, integrationData)
  },

  /**
   * Updates an existing integration.
   * @param {Object} integrationData - Data for the integration to update, including the ID.
   * @returns {Promise} - Promise representing the updated integration.
   */
  updateIntegration(integrationData) {
    if (!integrationData || typeof integrationData !== 'object') {
      throw new Error('Integration update requires valid data.')
    }
    if (!integrationData.id) {
      throw new Error('Integration update requires an \'id\' field.')
    }

    return session.put(`${APIEndpoint}/api/integration/${integrationData.id}/`,
      integrationData)
  },

  /**
   * Deletes a integration by ID.
   * @param {Number|String} integrationId - The ID of the integration to delete.
   * @returns {Promise} - Promise representing the deletion operation.
   */
  deleteIntegration(integrationId) {
    if (!integrationId) {
      throw new Error('Integration deletion requires a valid ID.')
    }
    return session.delete(`${APIEndpoint}/api/integration/${integrationId}/`)
  },

  /**
   * Initiates the Mailchimp OAuth flow by fetching the authorization URL.
   * @returns {Promise} - Promise representing the authorization URL.
   */
  getMailchimpAuthUrl(data) {
    return session.post(`${APIEndpoint}/api/mailchimp/authorize/`, data)
  },

  /**
   * Fetches Mailchimp lists associated with a Mailchimp integration.
   * @param {Number|String} integrationId - The ID of the Mailchimp integration.
   * @returns {Promise} - Promise representing the list of Mailchimp lists.
   */
  fetchMailchimpLists(integrationId) {
    if (!integrationId) {
      throw new Error('Fetching Mailchimp lists requires a valid integration ID.');
    }

    return session.get(`${APIEndpoint}/api/mailchimp/lists/`, {
      params: { integration_id: integrationId },
    });
  },

  /**
   * Fetches Mailchimp merge fields for a specific list associated with a Mailchimp integration.
   * @param {Number|String} integrationId - The ID of the Mailchimp integration.
   * @param {String} listId - The ID of the Mailchimp list.
   * @returns {Promise} - Promise representing the list of Mailchimp merge fields.
   */
  fetchMailchimpMergeFields(integrationId, listId) {
    if (!integrationId || !listId) {
      throw new Error('Fetching Mailchimp merge fields requires a valid integration ID and list ID.');
    }

    return session.get(`${APIEndpoint}/api/mailchimp/merge-fields/`, {
      params: { integration_id: integrationId, list_id: listId },
    });
  },

    /**
   * Fetches ActiveCampaign lists associated with an ActiveCampaign integration.
   * @param {Number|String} integrationId - The ID of the ActiveCampaign integration.
   * @returns {Promise} - Promise representing the list of ActiveCampaign lists.
   */
  fetchActiveCampaignLists(integrationId) {
    if (!integrationId) {
      throw new Error('Fetching ActiveCampaign lists requires a valid integration ID.');
    }

    return session.get(`${APIEndpoint}/api/activecampaign/lists/`, {
      params: { integration_id: integrationId },
    });
  },

  /**
   * Fetches ActiveCampaign merge fields for a specific list associated with an ActiveCampaign integration.
   * @param {Number|String} integrationId - The ID of the ActiveCampaign integration.
   * @param {String} listId - The ID of the ActiveCampaign list.
   * @returns {Promise} - Promise representing the list of ActiveCampaign merge fields.
   */
  fetchActiveCampaignMergeFields(integrationId, listId) {
    if (!integrationId || !listId) {
      throw new Error('Fetching ActiveCampaign merge fields requires a valid integration ID and list ID.');
    }

    return session.get(`${APIEndpoint}/api/activecampaign/merge-fields/`, {
      params: { integration_id: integrationId, list_id: listId },
    });
  },

  // /**
  //  * Fetches a specific integration by ID.
  //  * @param {Number|String} integrationId - The ID of the integration to retrieve.
  //  * @returns {Promise} - Promise representing the retrieved integration.
  //  */
  // getIntegration(integrationId) {
  //   if (!integrationId) {
  //     throw new Error("Fetching a integration requires a valid ID.");
  //   }
  //   return session.get(`${APIEndpoint}/api/integration/${integrationId}/`)
  // },
}